import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-endometrial-microbiome-testing"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "Endometrial Microbiome Testing",
  subtitle: "Biopsy-based microbiome add-on, including EMMA-style tests",
  icon: "🧫",
  body: {
    testName: "Endometrial Microbiome Testing",
    category: "biopsy",
    evidenceStrength: "debunked",
    whatItMeasures:
      "Maps bacterial DNA found in an endometrial biopsy sample, including Lactobacillus and other detected bacteria.\n\nEMMA is the Igenomix-branded version; CooperSurgical's ERBiome is another branded endometrial microbiome test.\n\nThese tests can describe the sample microbiome, but NICE found no eligible evidence that microbiological analysis tests improve fertility treatment outcomes.",
    howItWorks:
      "A clinician takes an endometrial biopsy. The lab sequences or quantifies bacterial DNA, estimates microbiome composition, and may suggest antibiotics or probiotics. Some branded versions bundle chronic endometritis bacteria detection into the same report. The key limitation is that detecting bacteria does not prove that testing and treating the result improves live birth.",
    quickFacts: [
      { kind: "sample", value: "Endometrial tissue biopsy" },
      { kind: "time", value: "5-10 min biopsy" },
      { kind: "pain", value: "Cramping during and shortly after" },
      { kind: "anesthesia", value: "Usually none" },
      { kind: "prep", value: "No fasting usually; ask about ibuprofen" },
      { kind: "results", value: "2-3 weeks" },
    ],
    resultTypes: [
      {
        label: "Lactobacillus-dominant",
        meaning:
          "The report shows a microbiome mainly made up of Lactobacillus species.",
        whatToExpect:
          "This is usually treated as a reassuring microbiome pattern, but it still does not prove implantation will happen or that microbiome testing improves live birth.",
      },
      {
        label: "Dysbiotic",
        meaning:
          "The report shows lower Lactobacillus and more non-Lactobacillus bacteria than expected.",
        whatToExpect:
          "The lab or clinic may suggest probiotics or antibiotics. Ask what exact organism, threshold, and evidence justify treatment before delaying transfer.",
      },
      {
        label: "Pathogen-associated",
        meaning:
          "Specific bacteria linked with infection or chronic endometritis may be detected.",
        whatToExpect:
          "This overlaps with ALICE-style interpretation. Ask whether chronic endometritis is actually suspected and how treatment success would be checked.",
      },
      {
        label: "Low biomass or inconclusive",
        meaning:
          "There may be too little bacterial DNA or too much uncertainty to interpret the sample confidently.",
        whatToExpect:
          "Your clinic may discuss repeating the biopsy or doing nothing. Avoid treating a noisy or inconclusive sample as a clear diagnosis.",
      },
    ],
    expectations: {
      canExpect: [
        "A uterine lining biopsy with DNA sequencing of bacterial material",
        "A report describing Lactobacillus percentage and other detected bacteria",
        "Possible antibiotic or probiotic suggestions from the lab or clinician",
      ],
      shouldNotExpect: [
        "Testing and treating the microbiome has not been proven to improve live birth",
        "Routine use can delay transfer without a specific indication",
        "Low-biomass samples can be noisy and may not clearly separate infection from sample contamination",
      ],
    },
    considerations: [
      "NICE found no eligible effectiveness evidence for microbiological analysis tests such as EMMA or ALICE as fertility treatment add-ons",
      "A biopsy adds discomfort, bleeding, infection risk, cost, and often a cycle delay",
      "If chronic endometritis is suspected, ask how the diagnosis will be confirmed and how treatment success will be checked",
      "Do not treat this as a routine pre-transfer screen unless your doctor can name a specific indication",
      "Ask whether your clinic is ordering a branded test such as EMMA or ERBiome, or a different lab-developed microbiome panel",
    ],
    bestFor: [
      "Research setting",
      "Selected recurrent implantation failure cases only after standard causes are reviewed",
      "Not recommended as a routine IVF add-on",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "NICE found no evidence on effectiveness for microbiological analysis tests such as EMMA or ALICE",
        mechanism:
          "The test may detect bacteria, but detection does not prove that treating the result improves live birth",
        source: "NICE NG257 evidence review, 2026",
      },
      {
        direction: "neutral",
        outcome:
          "Observational studies suggest an association between endometrial microbiome patterns and outcomes, but they do not prove the test changes outcomes",
        source: "Moreno et al., Microbiome 2022 (observational association)",
      },
      {
        direction: "negative",
        outcome:
          "ESHRE 2023 add-ons recommendations and HFEA do not endorse routine microbiome testing - RCT evidence is absent",
        source: "ESHRE add-ons recommendations, Human Reproduction 2023",
      },
    ],
    soldAs: [
      {
        name: "EMMA",
        company: "Igenomix",
        description:
          "A branded endometrial microbiome test that sequences bacterial DNA from an endometrial biopsy sample.",
        evidenceSummary:
          "Can describe the microbiome profile, but product-specific evidence has not shown routine live birth benefit.",
        linkedTestId: makeId(
          "test-endometrial-microbiome-testing-products-emma",
        ),
      },
      {
        name: "ERBiome",
        company: "CooperSurgical",
        description:
          "A branded endometrial microbiome test that can be ordered alone or as part of CooperSurgical's ER-Complete bundle.",
        evidenceSummary:
          "Belongs to the same microbiome-testing category where independent guidance has not found effectiveness evidence for routine fertility use.",
        linkedTestId: makeId(
          "test-endometrial-microbiome-testing-products-erbiome",
        ),
      },
    ],
    headlineStat: "No proven live birth benefit",
    turnaroundTime: "2-3 weeks",
    costRange: { low: 400, high: 900 },
    atHome: false,
    clinicRequired: true,
    cycleDay:
      "Biopsy timing depends on whether microbiome testing is standalone or bundled with receptivity testing; confirm mock-cycle timing with the clinic.",
    dataPoints: [],
    section: "implantation",
  },
  sources: [
    {
      title:
        "Fertility problems: assessment and treatment, evidence review D, endometrial receptivity testing",
      journal: "NICE NG257",
      year: 2026,
      url: "https://www.nice.org.uk/guidance/ng257/evidence/d-endometrial-receptivity-testing-pdf-15664473329",
      evidenceLevel: "guideline",
    },
    {
      title: "EMMA Endometrial Microbiome Metagenomic Analysis",
      journal: "Igenomix product information",
      year: 2026,
      url: "https://www.igenomix.eu/our-services/emma-endometrial-microbiome-metagenomic-analysis/",
      evidenceLevel: "expert-opinion",
    },
    {
      title: "ER-Complete and ERBiome endometrial microbiome testing",
      journal: "CooperSurgical product information",
      year: 2026,
      url: "https://www.coopersurgical.com/patient-article/er-complete-test-for-patients/",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "Endometrial microbiota composition is associated with reproductive outcome in infertile patients",
      authors: "Moreno I et al.",
      journal: "Microbiome",
      year: 2022,
      url: "https://pubmed.ncbi.nlm.nih.gov/34980280/",
      evidenceLevel: "cohort",
    },
    {
      title:
        "Good practice recommendations on add-ons in reproductive medicine",
      authors: "ESHRE Add-ons working group; Lundin K, Bentzen JG, et al.",
      journal: "Human Reproduction",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37747409/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 4,
};

registerBlock(test);

const emmaProduct: ContentBlock<"test"> = {
  id: makeId("test-endometrial-microbiome-testing-products-emma"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "EMMA",
  subtitle: "Commercial test profile for endometrial microbiome testing",
  icon: "🧫",
  body: {
    testName: "EMMA (Endometrial Microbiome Metagenomic Analysis)",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-endometrial-microbiome-testing"),
      company: "Igenomix",
      productUrl:
        "https://www.igenomix.eu/our-services/emma-endometrial-microbiome-metagenomic-analysis/",
      marketedNames: ["EMMA", "Endometrial Microbiome Metagenomic Analysis"],
      availability: [
        {
          region: "Patient access",
          detail:
            "Usually ordered through a fertility clinic after an endometrial biopsy; availability depends on the clinic and Igenomix lab route.",
        },
      ],
    },
    whatItMeasures:
      "EMMA is the Igenomix-branded endometrial microbiome test. It sequences bacterial DNA from an endometrial biopsy sample and reports Lactobacillus percentage and other detected bacteria.",
    howItWorks: test.body.howItWorks,
    quickFacts: test.body.quickFacts,
    resultTypes: test.body.resultTypes,
    expectations: test.body.expectations,
    considerations: test.body.considerations,
    bestFor: test.body.bestFor,
    keyFindings: test.body.keyFindings,
    headlineStat: test.body.headlineStat,
    turnaroundTime: test.body.turnaroundTime,
    costRange: test.body.costRange,
    atHome: false,
    clinicRequired: true,
    cycleDay: test.body.cycleDay,
    dataPoints: [],
    section: "implantation",
  },
  sources: test.sources,
  readTimeMinutes: 4,
};

const erBiomeProduct: ContentBlock<"test"> = {
  id: makeId("test-endometrial-microbiome-testing-products-erbiome"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "ERBiome",
  subtitle: "Commercial test profile for endometrial microbiome testing",
  icon: "🧫",
  body: {
    testName: "ERBiome",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-endometrial-microbiome-testing"),
      company: "CooperSurgical",
      productUrl:
        "https://www.coopersurgical.com/patient-article/er-complete-test-for-patients/",
      marketedNames: ["ERBiome"],
      availability: [
        {
          region: "Patient access",
          detail:
            "Usually ordered through a fertility clinic after an endometrial biopsy; availability depends on the clinic and CooperSurgical genetics workflow.",
        },
      ],
    },
    whatItMeasures:
      "ERBiome is CooperSurgical's branded endometrial microbiome test. It belongs to the same category as EMMA: biopsy-based testing that reports bacterial DNA patterns in the uterine lining.",
    howItWorks: test.body.howItWorks,
    quickFacts: test.body.quickFacts,
    resultTypes: test.body.resultTypes,
    expectations: test.body.expectations,
    considerations: [
      "Ask whether ERBiome is being ordered alone or as part of ER-Complete.",
      "Ask what direct ERBiome evidence the clinic is relying on, not just general microbiome associations.",
      "Ask what treatment would change if the result is abnormal or inconclusive.",
    ],
    bestFor: test.body.bestFor,
    keyFindings: test.body.keyFindings,
    headlineStat: test.body.headlineStat,
    turnaroundTime: test.body.turnaroundTime,
    costRange: test.body.costRange,
    atHome: false,
    clinicRequired: true,
    cycleDay: test.body.cycleDay,
    dataPoints: [],
    section: "implantation",
  },
  sources: test.sources,
  readTimeMinutes: 4,
};

registerBlock(emmaProduct);
registerBlock(erBiomeProduct);

registerRelationship({
  from: makeId("test-endometrial-microbiome-testing"),
  to: makeId("test-endometrial-microbiome-testing-products-emma"),
  type: "related-to",
  label: "Sold as",
});

registerRelationship({
  from: makeId("test-endometrial-microbiome-testing"),
  to: makeId("test-endometrial-microbiome-testing-products-erbiome"),
  type: "related-to",
  label: "Sold as",
});
